import { useState, type CSSProperties, type ReactNode } from "react";
import { logoBackdropClasses } from "./Internal/logoBackdrop";

interface ImageProps {
  src: string;
  /** Describe the picture. Pass "" for a purely decorative image so screen
   *  readers skip it — the attribute is required either way. */
  alt: string;
  /**
   * Fixed width in px. Use for a glyph-like image whose size is part of its
   * meaning — a file-type badge, a logo beside a label.
   */
  width?: number | undefined;
  /**
   * Cap the width in px while still filling a narrower container. Use for an
   * illustration or screenshot that should shrink on a phone but not blow up
   * on a wide screen.
   */
  maxWidth?: number | undefined;
  /**
   * Fixed height in px. The counterpart to `width`, and what a logo usually
   * wants: logos vary wildly in aspect ratio, so pinning the height and letting
   * the width follow is what makes a row of them line up.
   */
  height?: number | undefined;
  /** Cap the height in px. The counterpart to `maxWidth`. */
  maxHeight?: number | undefined;
  /**
   * How the picture fills its box when both dimensions are constrained.
   * `contain` letterboxes it and never crops — the right answer for a logo,
   * where cropping would clip the mark. `cover` fills and crops, for a photo.
   * Only meaningful alongside a size constraint.
   */
  fit?: "contain" | "cover" | undefined;
  /**
   * Where the picture sits in its box when `contain` leaves slack. Defaults to
   * centred; `left` is what a logo in a logo-and-label row wants.
   */
  align?: "left" | "center" | "right" | undefined;
  /**
   * Treat this as a third-party mark — a carrier, bank or card logo. Two things
   * follow, and both are the kind of thing every call site otherwise
   * reimplements: the mark gets a white pill in dark mode (the artwork is drawn
   * for a light page and is either invisible or glaring on a dark one), and a
   * missing file renders nothing rather than a broken-image glyph, which is the
   * right answer for a carrier we have no logo for.
   */
  logo?: boolean | undefined;
  /**
   * What to show in the picture's place when the file fails to load. Without
   * it a `logo` renders nothing; with it the caller keeps its slot, so a row of
   * marks where one is missing still lines up.
   */
  fallback?: ReactNode | undefined;
  /** Native hover tooltip. Use for a mark whose name isn't already beside it. */
  title?: string | undefined;
}

const objectPositions = {
  left: "left center",
  center: "center",
  right: "right center",
} as const;

type ImageErrorHandler = (e: { currentTarget: HTMLImageElement }) => void;

/**
 * What a failed load does. With a fallback the caller's replacement takes the
 * picture's place; a bare logo hides itself; anything else shows the browser's
 * broken-image glyph, which is right for an illustration we expected to exist.
 */
function errorHandlerFor(
  logo: boolean | undefined,
  hasFallback: boolean,
  markFailed: () => void,
): ImageErrorHandler | undefined {
  if (hasFallback) return () => markFailed();
  if (logo === true) {
    return (e) => {
      e.currentTarget.style.display = "none";
    };
  }
  return undefined;
}

/**
 * A picture: a screenshot, an illustration, a logo. Sizes come from props
 * rather than utility classes so images stay consistent across screens, and
 * so feature code never needs a `className` to place one.
 *
 * With no size given it fills its container. Constrain it with any of `width`,
 * `height`, `maxWidth` or `maxHeight`, and use `fit` to say what happens to the
 * aspect ratio when you constrain both.
 */
export function Image({
  src,
  alt,
  width,
  maxWidth,
  height,
  maxHeight,
  fit,
  align,
  logo,
  fallback,
  title,
}: ImageProps) {
  // Remember which file failed, not just that one did: the same instance can
  // be handed a new `src` (a table row re-keyed by index, a header switching
  // carriers), and that picture deserves its own attempt.
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);
  const onError = errorHandlerFor(logo, fallback !== undefined, () =>
    setFailedSrc(src),
  );
  if (failedSrc === src) {
    return <>{fallback}</>;
  }
  // A *fixed* dimension replaces filling the container; a *ceiling* does not —
  // `maxWidth` alone still means "fill, but stop there", which is the whole
  // point of a cap. Getting this backwards makes a capped illustration shrink to
  // its natural size instead of filling a narrower phone screen.
  const fixed = width !== undefined || height !== undefined;

  // These are numbers the caller chose or measured, not design tokens, so they
  // ride through as a CSSProperties value rather than Tailwind classes — there
  // is no spacing scale that covers "as tall as this row of carrier logos".
  const style: CSSProperties = {
    ...(width !== undefined && { width }),
    ...(height !== undefined && { height }),
    ...(maxWidth !== undefined && { maxWidth }),
    ...(maxHeight !== undefined && { maxHeight }),
    ...(fit !== undefined && { objectFit: fit }),
    ...(align !== undefined && { objectPosition: objectPositions[align] }),
  };

  // A logo never fills. "Fill, but stop there" is right for an illustration and
  // wrong for a third-party mark: `w-full` + `maxHeight` with no `object-fit`
  // stretches it to the container's width and clamps its height, which skews the
  // artwork. Ceilings alone let the browser scale it proportionally — what the
  // Cargo original did, and the only way a mark whose aspect ratio we don't
  // control comes out looking like itself.
  const classes = [
    fixed || logo ? "" : "w-full",
    logo ? logoBackdropClasses : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <img
      src={src}
      alt={alt}
      {...(title !== undefined && { title })}
      {...(classes !== "" && { className: classes })}
      style={style}
      {...(onError !== undefined && { onError })}
    />
  );
}
